import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

interface ReadReceipt {
  messageId: string;
  userId: string;
  readAt: string;
}

interface MessageReadStatus {
  [messageId: string]: ReadReceipt[];
}

export function useReadReceipts(messageIds: string[], type: 'chat_room' | 'conversation') {
  const { user } = useAuth();
  const [readReceipts, setReadReceipts] = useState<MessageReadStatus>({});

  // Mark message as read
  const markAsRead = async (messageId: string) => {
    if (!user) return;

    try {
      if (type === 'chat_room') {
        // For chat rooms, use the new read receipts table
        const { error } = await supabase
          .from('chat_message_read_receipts')
          .upsert({
            message_id: messageId,
            user_id: user.id,
            read_at: new Date().toISOString()
          }, { 
            onConflict: 'message_id,user_id',
            ignoreDuplicates: false 
          });

        if (error) {
          console.error('Error marking chat message as read:', error);
        }
      } else {
        // For conversations, update the existing messages table
        const { error } = await supabase
          .from('messages')
          .update({ is_read: true })
          .eq('id', messageId)
          .neq('sender_id', user.id);

        if (error) {
          console.error('Error marking conversation message as read:', error);
        }
      }
    } catch (error) {
      console.error('Error in markAsRead:', error);
    }
  };

  // Mark multiple messages as read (for when user opens a conversation/room)
  const markMultipleAsRead = async (messageIds: string[]) => {
    if (!user || messageIds.length === 0) return;

    try {
      if (type === 'chat_room') {
        // For chat rooms, insert multiple read receipts
        const receipts = messageIds.map(messageId => ({
          message_id: messageId,
          user_id: user.id,
          read_at: new Date().toISOString()
        }));

        const { error } = await supabase
          .from('chat_message_read_receipts')
          .upsert(receipts, { 
            onConflict: 'message_id,user_id',
            ignoreDuplicates: true 
          });

        if (error) {
          console.error('Error marking multiple chat messages as read:', error);
        }
      } else {
        // For conversations, update multiple messages
        const { error } = await supabase
          .from('messages')
          .update({ is_read: true })
          .in('id', messageIds)
          .neq('sender_id', user.id);

        if (error) {
          console.error('Error marking multiple conversation messages as read:', error);
        }
      }
    } catch (error) {
      console.error('Error in markMultipleAsRead:', error);
    }
  };

  // Fetch read receipts for chat rooms
  useEffect(() => {
    if (type !== 'chat_room' || messageIds.length === 0) return;

    const fetchReadReceipts = async () => {
      try {
        const { data, error } = await supabase
          .from('chat_message_read_receipts')
          .select(`
            message_id,
            user_id,
            read_at,
            profiles:user_id (
              first_name,
              last_name
            )
          `)
          .in('message_id', messageIds);

        if (error) {
          console.error('Error fetching read receipts:', error);
          return;
        }

        // Group by message ID
        const grouped = data?.reduce((acc, receipt) => {
          if (!acc[receipt.message_id]) {
            acc[receipt.message_id] = [];
          }
          acc[receipt.message_id].push({
            messageId: receipt.message_id,
            userId: receipt.user_id,
            readAt: receipt.read_at
          });
          return acc;
        }, {} as MessageReadStatus) || {};

        setReadReceipts(grouped);
      } catch (error) {
        console.error('Error fetching read receipts:', error);
      }
    };

    fetchReadReceipts();
  }, [messageIds, type]);

  // Set up real-time subscription for read receipts
  useEffect(() => {
    if (type !== 'chat_room' || messageIds.length === 0) return;

    const channel = supabase
      .channel('read-receipts')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_message_read_receipts',
          filter: `message_id=in.(${messageIds.join(',')})`
        },
        (payload) => {
          const newReceipt = payload.new as any;
          setReadReceipts(prev => ({
            ...prev,
            [newReceipt.message_id]: [
              ...(prev[newReceipt.message_id] || []),
              {
                messageId: newReceipt.message_id,
                userId: newReceipt.user_id,
                readAt: newReceipt.read_at
              }
            ]
          }));
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [messageIds, type]);

  return {
    readReceipts,
    markAsRead,
    markMultipleAsRead
  };
}